import { useEffect } from 'react';

const SITE_URL = 'https://www.revvly.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/machine-poster.jpg`;

export interface SeoOptions {
  /** Document title and og:title / twitter:title. */
  title: string;
  /** Meta description and og:description / twitter:description. */
  description: string;
  /** Site-relative path, e.g. `/blog/mrr-gap`. Used to build canonical + og:url. */
  path: string;
  /** Absolute image URL. Defaults to the site's standard OG image. */
  image?: string;
  /** og:type. Defaults to 'article'. */
  type?: 'website' | 'article';
  /** Page-specific structured data, rendered as its own `<script type="application/ld+json">`. */
  jsonLd?: Record<string, unknown>;
}

/** Find-or-create a `<meta>` tag by attribute, returning enough state to restore it later. */
function upsertMeta(attrName: 'name' | 'property', attrValue: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attrName}="${attrValue}"]`);
  const previousContent = el?.getAttribute('content') ?? null;
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return { el, created, previousContent };
}

/**
 * Sets document title, meta description, canonical link, Open Graph / Twitter
 * tags, and (optionally) a page-specific JSON-LD script for the lifetime of
 * the calling component. Restores whatever was previously in `<head>` (the
 * static tags from index.html, or another page's tags) on unmount, so
 * navigating between routes never leaves stale metadata behind.
 *
 * This is the SPA-appropriate equivalent of per-page `<Head>`/Helmet: there's
 * no SSR here, so these tags only exist for clients that execute JS (which
 * covers Googlebot and social-preview unfurlers that render before scraping).
 */
export function useSeo({ title, description, path, image, type = 'article', jsonLd }: SeoOptions) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : undefined;

  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const ogImage = image ?? DEFAULT_OG_IMAGE;
    const previousTitle = document.title;
    document.title = title;

    const tags = [
      upsertMeta('name', 'description', description),
      upsertMeta('property', 'og:type', type),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:title', title),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:image', ogImage),
      upsertMeta('name', 'twitter:title', title),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', ogImage),
    ];

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const previousCanonical = canonical?.getAttribute('href') ?? null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    let jsonLdEl: HTMLScriptElement | null = null;
    if (jsonLdString) {
      // A pre-rendered page (scripts/prerender-website.js) ships its own copy of this tag. Once the
      // app has booted it owns the tag, so drop the static one rather than leave two.
      document.head.querySelectorAll('script[data-seo-jsonld="page"]').forEach((el) => el.remove());
      jsonLdEl = document.createElement('script');
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.setAttribute('data-seo-jsonld', 'page');
      jsonLdEl.textContent = jsonLdString;
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = previousTitle;
      for (const { el, created, previousContent } of tags) {
        if (created) {
          el.remove();
        } else if (previousContent !== null) {
          el.setAttribute('content', previousContent);
        }
      }
      if (canonicalCreated) {
        canonical?.remove();
      } else if (previousCanonical !== null) {
        canonical?.setAttribute('href', previousCanonical);
      }
      jsonLdEl?.remove();
    };
  }, [title, description, path, image, type, jsonLdString]);
}
